import React, { FC, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { AdvancedSelect } from '../../../../widgets/select';
import Loader from '../../../../widgets/loader';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { Link } from '../../../../widgets/buttons';
import Stack from '../../../../widgets/stack/stack';
import { duplicateDialogSalesChannelsQuery } from './__generated__/duplicateDialogSalesChannelsQuery.graphql';
import { T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { merchantProduct_item$data } from '../../state/queries/__generated__/merchantProduct_item.graphql';
import StoreCatalog from '../../storeCatalog/storeCatalog';
import { Dialog, DialogWindow, QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { ValidationBox } from '../../../../widgets/validation/validationBox';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';

const CustomDialogWindow = styled(DialogWindow)`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
    margin: 0 80px;
`;

const DuplicateDialogContent: FC<{
    sourceProductCode: string;
    salesChannelID: string;
    onConfirm: (skus: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>[], destinationSalesChannelID: string) => void;
}> = ({ sourceProductCode, salesChannelID, onConfirm }) => {
    const { salesChannels } = useLazyLoadQuery<duplicateDialogSalesChannelsQuery>(
        graphql`
            query duplicateDialogSalesChannelsQuery {
                salesChannels(status: ACTIVE, ignoreNoneECommerce: true) {
                    id
                    detail {
                        properties {
                            name
                        }
                    }
                    ecommerceSettings {
                        ecommerceName
                        remoteUrl
                        hasProductsApi
                    }
                }
            }
        `,
        {}
    );
    const channelOptions = salesChannels
        .filter((channel) => channel.ecommerceSettings && channel.ecommerceSettings.ecommerceName !== 'NONE')
        .map((channel) => ({
            value: channel.id,
            label: channel.detail?.properties?.name || channel.ecommerceSettings?.remoteUrl || channel.ecommerceSettings?.ecommerceName || channel.id,
            isDisabled: channel.ecommerceSettings?.ecommerceName === 'API' && !channel.ecommerceSettings.hasProductsApi
        }));
    const unavailableChannels = channelOptions.filter((channel) => channel.isDisabled);
    const [destinationSalesChannelID, setDestinationSalesChannelID] = useState(
        channelOptions.find((channel) => channel.value === salesChannelID && !channel.isDisabled)?.value ??
            channelOptions.find((channel) => !channel.isDisabled)?.value ??
            ''
    );
    const [selectedProducts, setSelectedProducts] = useState<Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>[]>([]);
    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>();
    const { showDialog, closeDialog } = useDialogManager();
    const validationContext = useValidationContext();
    const salesChannelSelector = (
        <AdvancedSelect
            options={channelOptions}
            value={channelOptions.find((channel) => channel.value === destinationSalesChannelID)}
            placeholder={T._('Select sales channel', 'Admin')}
            aria-label={T._('Destination sales channel', 'Admin')}
            formatOptionLabel={(option) => (option.isDisabled ? `${option.label} — ${T._('Product Sync API required', 'Admin')}` : option.label)}
            onChange={(item) => {
                if (!item?.value || item.isDisabled || item.value === destinationSalesChannelID) return;
                setSelectedProducts([]);
                setDestinationSalesChannelID(item.value);
            }}
        />
    );

    const handleProductClick = async (merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => {
        if (selectedProducts.findIndex((x) => x.sku === merchantProduct.sku!) === -1) {
            setSelectedProducts([...selectedProducts, merchantProduct]);
        } else {
            setSelectedProducts(selectedProducts.filter((x) => x.sku !== merchantProduct.sku));
        }
    };

    const confirmProducts = (products: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>[]) => {
        if (products.length === 0) {
            setSelectedProducts([]);
            setTimeout(() => validationContext.submit(), 0);
            return;
        }

        onConfirm(products, destinationSalesChannelID);
    };

    const handleDuplicateClick = () => {
        if (!validationContext.submit()) return;

        if (selectedProducts.some((product) => product.isConfiguratedInZakeke))
            showDialog(
                'duplication-confirm',
                <QuestionDialog
                    message={
                        <span>
                            {T._(
                                'ATTENTION: you have selected one or more products already configured as customizable. If you proceed, the new personalization settings will overwrite the existing ones. Are you sure to proceed with all the selected products?',
                                'Admin'
                            )}
                        </span>
                    }
                    onYesClick={() => {
                        closeDialog('duplication-confirm');
                        confirmProducts(selectedProducts);
                    }}
                    onNoClick={() => {
                        const filteredProducts = selectedProducts.filter((product) => !product.isConfiguratedInZakeke);
                        closeDialog('duplication-confirm');
                        confirmProducts(filteredProducts);
                    }}
                    buttonYesLabel={T._('YES, duplicate on all selected products.', 'Admin')}
                    buttonNoLabel={T._('NO, duplicate only on not configured products.', 'Admin')}
                />
            );
        else confirmProducts(selectedProducts);
    };

    return (
        <Dialog
            windowDecorator={CustomDialogWindow}
            buttons={[{ label: T._('Next', 'Admin'), onClick: handleDuplicateClick, disabled: !destinationSalesChannelID }]}
            ref={(instance) => setScrollContainer(instance)}
        >
            <Stack>
                {unavailableChannels.length > 0 && (
                    <NotificationBanner
                        type='WARNING'
                        content={
                            <>
                                {unavailableChannels.map((channel) => channel.label).join(', ')}:{' '}
                                {T._('To duplicate products into these API sales channels, implement the Product Sync API.', 'Admin')}{' '}
                                <Link href='https://zakeke.zendesk.com/hc/en-us/articles/360010310554-Products-Sync-V3' target='_blank'>
                                    {T._('Product Sync API guide', 'Admin')}
                                </Link>
                            </>
                        }
                    />
                )}
                <ValidationBox
                    fullWidth
                    validationKey='duplicate-products'
                    validationValue={selectedProducts.length}
                    validate={(value) => value === 0}
                    validationMessage={T._('At least one product is required.', 'Admin')}
                >
                    {!destinationSalesChannelID ? (
                        <Stack>
                            {salesChannelSelector}
                            <NotificationBanner type='WARNING' content={T._('No sales channel with an available product catalog was found.', 'Admin')} />
                        </Stack>
                    ) : (
                        <StoreCatalog
                            cardTitle={T._('Select the products you want to duplicate on', 'Admin')}
                            salesChannelID={destinationSalesChannelID}
                            salesChannelSelector={salesChannelSelector}
                            onProductClick={handleProductClick}
                            scrollContainer={scrollContainer}
                            selectedProductsSKUs={selectedProducts.map((x) => x.sku!)}
                            showSelectionCheckboxes
                            showHeader={false}
                            showBackButton={false}
                            disableProductCode={destinationSalesChannelID === salesChannelID ? sourceProductCode : undefined}
                        />
                    )}
                </ValidationBox>
            </Stack>
        </Dialog>
    );
};

const DuplicateDialog: FC<React.ComponentProps<typeof DuplicateDialogContent>> = (props) => (
    <ValidationStateProvider>
        <Suspense
            fallback={
                <Dialog windowDecorator={CustomDialogWindow}>
                    <Loader />
                </Dialog>
            }
        >
            <DuplicateDialogContent {...props} />
        </Suspense>
    </ValidationStateProvider>
);

export default DuplicateDialog;
